/**
 * Universal icon color filter utility
 * Generates CSS filters to recolor icons to any target color
 */

/**
 * Converts a hex color to RGB values
 * @param {string} hex - Hex color code (e.g., '#2c3e50')
 * @returns {Object} Object with r, g, b values (0-255)
 */
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

/**
 * Generates a CSS filter string to recolor an image to a target color
 * Based on the algorithm from: https://codepen.io/sosuke/pen/Pjoqqp
 * 
 * @param {string} targetColor - Target color in hex format (e.g., '#2c3e50')
 * @returns {string} CSS filter string
 */
export function generateColorFilter(targetColor) {
    // Predefined filters for common colors (optimized)
    const predefinedFilters = {
        // Dark blue (#2c3e50) - main theme color
        '#2c3e50': 'brightness(0) saturate(100%) invert(23%) sepia(15%) saturate(1054%) hue-rotate(172deg) brightness(91%) contrast(87%)',
        // White
        '#ffffff': 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)',
        // Black
        '#000000': 'brightness(0) saturate(100%)',
    };

    const normalizedColor = targetColor.toLowerCase();
    if (predefinedFilters[normalizedColor]) {
        return predefinedFilters[normalizedColor];
    }

    // For other colors, use a simplified approach
    // This is a basic implementation that works well for most solid colors
    const rgb = hexToRgb(targetColor);
    if (!rgb) {
        console.warn(`Invalid color format: ${targetColor}`);
        return '';
    }

    // Calculate brightness and sepia values based on the target color
    const brightness = Math.max(rgb.r, rgb.g, rgb.b) / 255;
    const avgColor = (rgb.r + rgb.g + rgb.b) / 3;
    const saturation = Math.abs(Math.max(rgb.r, rgb.g, rgb.b) - Math.min(rgb.r, rgb.g, rgb.b)) / avgColor * 100;

    // Start with black base
    let filter = 'brightness(0) saturate(100%)';
    
    // Add invert if the color is lighter
    if (brightness > 0.5) {
        filter += ` invert(${Math.round(brightness * 100)}%)`;
    }
    
    // Add sepia and hue rotation for color
    if (saturation > 10) {
        filter += ` sepia(${Math.round(saturation)}%)`;
        
        // Calculate hue rotation based on dominant color channel
        let hueRotate = 0;
        if (rgb.r > rgb.g && rgb.r > rgb.b) {
            hueRotate = 0; // Red dominant
        } else if (rgb.g > rgb.r && rgb.g > rgb.b) {
            hueRotate = 85; // Green dominant
        } else if (rgb.b > rgb.r && rgb.b > rgb.g) {
            hueRotate = 200; // Blue dominant
        }
        
        filter += ` hue-rotate(${hueRotate}deg)`;
    }
    
    // Adjust final brightness
    filter += ` brightness(${Math.round(brightness * 100)}%)`;
    
    return filter;
}

/**
 * Icon color constants for the application
 */
export const iconColors = {
    primary: '#2c3e50',    // Main theme color (dark blue)
    white: '#ffffff',      // White for active states
    secondary: '#6B7280',  // Secondary/muted color
    health: '#FF4757',     // Health icon color
    mana: '#1E90FF',       // Mana icon color
};

/**
 * Pre-generated filters for common use cases
 */
export const iconFilters = {
    primary: generateColorFilter(iconColors.primary),
    white: generateColorFilter(iconColors.white),
    secondary: generateColorFilter(iconColors.secondary),
    health: generateColorFilter(iconColors.health),
    mana: generateColorFilter(iconColors.mana),
};

export default generateColorFilter;